import {
  Box,
  Grid,
  ListSubheader,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'

import Button from '@/components/button'
import InputText from '@/components/input-text'
import { Textarea } from '@/components/textarea'
import { useConfirmPulverizationActivity } from '@/hooks/mutations/use-confirm-pulverization-activity'
import { useGetPulverizationActivityById } from '@/hooks/queries/use-get-pulverization-activity-by-id'
import { routes } from '@/routes/config'

import { DiagnosisSummary } from '../../components/diagnosis-summary'
import { LoadingConfirmStep } from '../../components/loading-confirm-step'
import { useConfirmPulverizationForm } from './confirm-pulverization-form'
import { DefaultConfirmPulverizationForm } from './confirm-pulverization-schema'
import { useStyles } from './styles'

export interface ConfirmStepProps {
  onChangeCurrentStep: (step: number) => void
}

export function ConfirmStep({ onChangeCurrentStep }: ConfirmStepProps) {
  const classes = useStyles()
  const { activityId } = useParams<{ activityId: string }>()

  const { data } = useGetPulverizationActivityById(activityId)
  const { mutateAsync } = useConfirmPulverizationActivity()

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useConfirmPulverizationForm()

  function onSubmit(data: DefaultConfirmPulverizationForm) {
    return mutateAsync(
      {
        activityId: activityId!,
        pulverization_treatment: data.pulverization_treatment,
        start_date: data.date.start_date,
        end_date: data.date.end_date,
        description: data.description,
      },
      {
        onSuccess: () => onChangeCurrentStep(1),
      },
    )
  }

  useEffect(() => {
    if (data) {
      reset({
        pulverization_treatment:
          data.pulverization_treatment ||
          ('' as DefaultConfirmPulverizationForm['pulverization_treatment']),
        date: {
          start_date: data.start_date,
          end_date: data.end_date,
        },
        description: data.description ?? '',
      })
    }
  }, [data])

  return (
    <form
      className={classes.container}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h1'>Informações gerais</Typography>
        </Grid>

        <Grid item xs={7}>
          <Typography variant='subtitle2'>Resumo</Typography>
          {!data ? (
            <LoadingConfirmStep />
          ) : (
            <DiagnosisSummary diagnosis={data.diagnosis} />
          )}
        </Grid>

        <Grid item xs={5} className={classes.inputsContainer}>
          <Typography variant='subtitle2'>Confirmar a atividade</Typography>
          {!data ? (
            <LoadingConfirmStep />
          ) : (
            <>
              <Controller
                name='pulverization_treatment'
                control={control}
                defaultValue={'' as any}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <InputText
                    id='pulverization_treatment'
                    label={'Selecione o tipo de pulverização'}
                    select
                    required
                    SelectProps={{
                      MenuProps: {
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        getContentAnchorEl: null,
                        classes: { list: classes.menuList },
                      },
                    }}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    error={!!error}
                    helperText={error?.message}
                  >
                    <ListSubheader>Tratativas</ListSubheader>
                    <MenuItem value={'cromai_results'}>
                      Pulverização localizada com resultados Cromai
                    </MenuItem>
                    <MenuItem value={'total_area'}>
                      Pulverização da área total
                    </MenuItem>
                    <MenuItem value={'no_pulverizated'}>
                      Não foi realizado a pulverização
                    </MenuItem>
                  </InputText>
                )}
              />
              <Box display={'flex'} gridGap={'1rem'}>
                <InputText
                  id='start_date'
                  type='date'
                  label='Data inicial da realização'
                  required
                  InputLabelProps={{ shrink: true }}
                  onKeyDown={(e) => e.preventDefault()}
                  error={!!errors.date?.start_date}
                  helperText={errors.date?.start_date?.message}
                  {...register('date.start_date')}
                />
                <InputText
                  id='end_date'
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  onKeyDown={(e) => e.preventDefault()}
                  label='Data final da realização'
                  error={!!errors.date?.end_date}
                  helperText={errors.date?.end_date?.message}
                  {...register('date.end_date')}
                />
              </Box>
              <Controller
                control={control}
                name='description'
                defaultValue=''
                render={({ field, fieldState: { error } }) => (
                  <Textarea
                    aria-label='Descrição da atividade'
                    placeholder='Deixe anotações sobre a atividade'
                    value={field.value}
                    error={error?.message}
                    onChange={field.onChange}
                  />
                )}
              />
            </>
          )}
        </Grid>
      </Grid>

      <div className={classes.buttonsContainer}>
        <Button
          outlined
          component={Link}
          to={routes.Activities()}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button type='submit' disabled={!data} loading={isSubmitting}>
          Confirmar
        </Button>
      </div>
    </form>
  )
}
